import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Lock, Mail, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const expired = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("expired") === "1";
  const [error, setError] = useState(expired ? "Your session expired. Please log in again to continue editing." : "");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password.trim());
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF6F0]">
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB3ZWRkaW5nJTIwY2VsZWJyYXRpb24lMjBnb2xkZW4lMjBsaWdodHN8ZW58MHx8fHwxNzg5NDYxNTMzfDA&ixlib=rb-4.1.0&q=85"
          alt="Celebration"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#120E0C] via-black/50 to-black/30" />
        <div className="absolute bottom-0 p-12 text-amber-50">
          <Link to="/" className="flex items-center gap-2 mb-6">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600">
              <Sparkles className="h-5 w-5 text-white" />
            </span>
            <span className="font-serif text-2xl font-bold">Soulful Invites</span>
          </Link>
          <h2 className="font-serif text-3xl font-semibold max-w-md leading-snug">
            Manage your bespoke invitation catalogue.
          </h2>
          <p className="mt-3 text-amber-100/70 max-w-md">
            Add new video invites, organise them by event, and keep your gallery fresh.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <span className="grid place-items-center h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600">
              <Sparkles className="h-5 w-5 text-white" />
            </span>
            <span className="font-serif text-xl font-bold text-amber-950">Soulful Invites</span>
          </Link>

          <span className="text-xs tracking-widest uppercase font-semibold text-amber-700">Admin Access</span>
          <h1 className="mt-2 font-serif text-3xl font-bold text-amber-950">Welcome back</h1>
          <p className="mt-2 text-stone-600">Sign in to manage your invitation gallery.</p>

          <form data-testid="admin-login-form" onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-amber-900 font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-600" />
                <Input
                  id="email"
                  data-testid="admin-email-input"
                  type="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@soulfulinvites.com"
                  required
                  className="h-12 pl-10 bg-white border-amber-500/30 focus-visible:ring-amber-500/40"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-amber-900 font-medium">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-600" />
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type={showPw ? "text" : "password"}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12 pl-10 pr-11 bg-white border-amber-500/30 focus-visible:ring-amber-500/40"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-600 hover:text-amber-800"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button
              data-testid="admin-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-amber-950 hover:bg-amber-900 text-amber-50 font-semibold text-base"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <Link to="/" className="block mt-6 text-center text-sm text-amber-700 hover:text-amber-900">
            ← Back to catalogue
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
